import {
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Accordion,
  Checkbox,
  ScrollArea,
  Flex,
  Center,
  Loader,
} from '@mantine/core';
import { useState, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { notifications } from '@mantine/notifications';
import { myFetch } from '@commons/fetch';
import { useAppDispatch } from '@store';
import { updateModel } from '@store/slices/modelsSlice';

interface ImportFieldsModalProps {
  model: any;
  onClose: (response: any) => void;
}

export const ImportFieldsModal = ({
  model,
  onClose,
}: ImportFieldsModalProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>([]);
  const [error, setError] = useState<string>('');
  const [isLoadingModels, setIsLoadingModels] = useState<boolean>(false);
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [availableModels, setAvailableModels] = useState<any[]>([]);

  // Get current model's field names (case-insensitive) for filtering
  const currentFieldNames = useMemo(() => {
    return (model as any)?.fields?.map((f: any) => f.name?.toLowerCase()) || [];
  }, [model]);

  useEffect(() => {
    if (!model) return;

    const fetchModels = async () => {
      setIsLoadingModels(true);
      try {
        const response = await myFetch(`/${model.owner}/models`);
        const otherModels = response.data.filter(
          (m: any) => m.id !== model.id && m.category === model.category
        );
        setAvailableModels(otherModels);
      } catch (err: any) {
        notifications.show({
          color: 'red',
          title: intl.formatMessage({ id: 'error' }),
          message: err?.message || intl.formatMessage({ id: 'error' }),
        });
      } finally {
        setIsLoadingModels(false);
      }
    };

    fetchModels();
  }, [model, intl]);

  // Filter models and fields to only show fields that don't exist in current model
  const filteredModels = useMemo(() => {
    return availableModels
      .map((modelItem) => {
        const availableFields = (modelItem.fields || []).filter(
          (field: any) =>
            field.name && !currentFieldNames.includes(field.name.toLowerCase())
        );
        return {
          ...modelItem,
          availableFields,
        };
      })
      .filter((modelItem) => modelItem.availableFields.length > 0);
  }, [availableModels, currentFieldNames]);

  // Get all field IDs for easy lookup
  const allFieldIds = useMemo(() => {
    const ids: string[] = [];
    filteredModels.forEach((modelItem) => {
      modelItem.availableFields.forEach((field: any, index: number) => {
        ids.push(`field-${modelItem.id}-${field.name}-${index}`);
      });
    });
    return ids;
  }, [filteredModels]);

  const handleClose = () => {
    setSelectedFieldIds([]);
    setError('');
    onClose(null);
  };

  const handleImport = async () => {
    if (selectedFieldIds.length === 0) {
      setError(intl.formatMessage({ id: 'selectAtLeastOneField' }));
      return;
    }

    // Collect selected field objects
    const fieldsToImport: any[] = [];
    filteredModels.forEach((modelItem) => {
      modelItem.availableFields.forEach((field: any, index: number) => {
        const fieldId = `field-${modelItem.id}-${field.name}-${index}`;
        if (selectedFieldIds.includes(fieldId)) {
          fieldsToImport.push(field);
        }
      });
    });

    if (fieldsToImport.length === 0) {
      setError(intl.formatMessage({ id: 'noFieldsToImport' }));
      return;
    }

    try {
      setIsImporting(true);

      const response = await myFetch(
        `/${model.owner}/models/${model.id}/import-fields`,
        {
          method: 'POST',
          body: JSON.stringify({
            fields: fieldsToImport,
          }),
        }
      );

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage({ id: 'importedFields' }),
      });

      // Update the model in Redux store
      if (response?.data && model?.owner) {
        await dispatch(
          updateModel({
            username: model.owner,
            modelId: model.id,
            modelData: response.data,
          })
        ).unwrap();
      }

      handleClose();
    } catch (err: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: err?.message || intl.formatMessage({ id: 'error' }),
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleSelectAll = () => {
    setError('');
    setSelectedFieldIds(allFieldIds);
  };

  const handleDeselectAll = () => {
    setError('');
    setSelectedFieldIds([]);
  };

  if (!model) {
    return null;
  }

  return (
    <Modal
      opened={model !== null}
      onClose={handleClose}
      title={intl.formatMessage({ id: 'importFields' })}
      centered
      size="lg"
      withCloseButton={!isImporting}
      closeOnClickOutside={!isImporting}
      closeOnEscape={!isImporting}
    >
      <Stack gap="md">
        {isLoadingModels ? (
          <Center h={250}>
            <Loader />
          </Center>
        ) : filteredModels.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            {availableModels.length === 0
              ? intl.formatMessage({ id: 'noModelsAvailable' })
              : intl.formatMessage({
                  id: 'noFieldsToImport',
                  defaultMessage:
                    'No fields available to import (all fields already exist)',
                })}
          </Text>
        ) : (
          <>
            <Group gap="xs">
              <Button
                size="xs"
                variant="light"
                onClick={handleSelectAll}
                disabled={
                  selectedFieldIds.length === allFieldIds.length || isImporting
                }
              >
                {intl.formatMessage({
                  id: 'selectAll',
                  defaultMessage: 'Select All',
                })}
              </Button>
              <Button
                size="xs"
                variant="light"
                onClick={handleDeselectAll}
                disabled={selectedFieldIds.length === 0 || isImporting}
              >
                {intl.formatMessage({
                  id: 'deselectAll',
                  defaultMessage: 'Deselect All',
                })}
              </Button>
            </Group>

            <ScrollArea h={400}>
              <Accordion variant="separated" radius="md">
                {filteredModels.map((modelItem) => {
                  const modelFieldIds = modelItem.availableFields.map(
                    (field: any, index: number) =>
                      `field-${modelItem.id}-${field.name}-${index}`
                  );
                  const allModelFieldsSelected = modelFieldIds.every(
                    (id: string) => selectedFieldIds.includes(id)
                  );
                  const someModelFieldsSelected = modelFieldIds.some(
                    (id: string) => selectedFieldIds.includes(id)
                  );

                  return (
                    <Accordion.Item key={modelItem.id} value={modelItem.id}>
                      <Accordion.Control
                        onClick={(e) => {
                          // Prevent accordion toggle when clicking on checkbox
                          const target = e.target as HTMLElement;
                          if (
                            target.tagName === 'INPUT' ||
                            target.tagName === 'LABEL' ||
                            target.closest('input[type="checkbox"]') ||
                            target.closest('label') ||
                            target.closest('[data-checkbox-wrapper]')
                          ) {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          }
                        }}
                      >
                        <Flex align="center" gap="xs">
                          <div
                            data-checkbox-wrapper
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                            }}
                            style={{ pointerEvents: 'auto' }}
                          >
                            <Checkbox
                              checked={allModelFieldsSelected}
                              indeterminate={
                                someModelFieldsSelected &&
                                !allModelFieldsSelected
                              }
                              disabled={isImporting}
                              onChange={(e) => {
                                setError('');
                                e.stopPropagation();
                                if (e.currentTarget.checked) {
                                  setSelectedFieldIds([
                                    ...selectedFieldIds.filter(
                                      (id) => !modelFieldIds.includes(id)
                                    ),
                                    ...modelFieldIds,
                                  ]);
                                } else {
                                  setSelectedFieldIds(
                                    selectedFieldIds.filter(
                                      (id) => !modelFieldIds.includes(id)
                                    )
                                  );
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                              label={`${modelItem.name} (${intl.formatMessage({
                                id: modelItem.type,
                              })})`}
                            />
                          </div>
                        </Flex>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap="xs" pl="md">
                          {modelItem.availableFields.map(
                            (field: any, index: number) => {
                              const fieldId = `field-${modelItem.id}-${field.name}-${index}`;
                              return (
                                <Checkbox
                                  key={fieldId}
                                  label={
                                    field.label ||
                                    field.title ||
                                    field.name ||
                                    `Field ${index + 1}`
                                  }
                                  checked={selectedFieldIds.includes(fieldId)}
                                  disabled={isImporting}
                                  onChange={(e) => {
                                    setError('');
                                    if (e.currentTarget.checked) {
                                      setSelectedFieldIds([
                                        ...selectedFieldIds,
                                        fieldId,
                                      ]);
                                    } else {
                                      setSelectedFieldIds(
                                        selectedFieldIds.filter(
                                          (id) => id !== fieldId
                                        )
                                      );
                                    }
                                  }}
                                />
                              );
                            }
                          )}
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            </ScrollArea>
          </>
        )}

        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}

        <Group justify="space-between" mt="md">
          <Text size="sm" c="dimmed">
            {selectedFieldIds.length > 0 &&
              intl.formatMessage(
                {
                  id: 'fieldsSelected',
                  defaultMessage: '{count} field(s) selected',
                },
                { count: selectedFieldIds.length }
              )}
          </Text>
          <Group>
            <Button
              variant="subtle"
              onClick={handleClose}
              disabled={isImporting}
            >
              {intl.formatMessage({ id: 'cancel' })}
            </Button>
            <Button
              onClick={handleImport}
              disabled={
                filteredModels.length === 0 ||
                selectedFieldIds.length === 0 ||
                isImporting ||
                isLoadingModels
              }
            >
              {intl.formatMessage({ id: 'import' })}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};
